import { handleActions } from 'redux-actions';
import { LOCATION_CHANGE, replace } from 'react-router-redux';
import { SubmissionError } from 'redux-form';

import cloneDeep from 'lodash/cloneDeep';
import orderBy from 'lodash/orderBy';
import values from  'lodash/values';
import find from 'lodash/find';
import get from 'lodash/get';
import forEach from 'lodash/forEach';

import http from '../shared/http';
import intl from '../shared/intl';

import {
	createSelectOptionsFromSysEnums
} from '../shared/form-elements/select';
import checkPermissions from '../shared/check-permissions';
import checkIsExcludedCountryCode from '../shared/checkIsExcludedCountryCode';

// actions types
const FETCH_DATA_SUCCESS = 'cpl-rules/fetch-data-success';
const FETCH_DATA_ERROR = 'cpl-rules/fetch-data-error';

const SUBMIT_SUCCESS = 'cpl-rules/submit-success';
const SUBMIT_ERROR = 'cpl-rules/submit-error';

const REMOVE_SUCCESS = 'cpl-rules/remove-success';
const REMOVE_ERROR = 'cpl-rules/remove-error';

const SORT_BY_COLUMN = 'cpl-rules/table-sort-by-column';
const SHOW_MORE_ROWS = 'cpl-rules/table-show-more-rows';

const TOGGLE_PROGRESS_BAR = 'cpl-rules/toggle-progress-bar';

const FETCH_OFFERS_SUCCESS = 'cpl-rules/fetch-offers-success';
const FETCH_OFFERS_ERROR = 'cpl-rules/fetch-offers-error';

const FETCH_GEOS_SUCCESS = 'cpl-rules/fetch-offers-success';
const FETCH_GEOS_ERROR = 'cpl-rules/fetch-offers-error';

const FETCH_AFFILIATES_SUCCESS = 'cpl-rules/fetch-affiliates-success';
const FETCH_AFFILIATES_ERROR = 'cpl-rules/fetch-affiliates-error';

// permissions
export const allowedPermissions = {
	'cplRulesList': 'cplRulesList',
	'cplRulesEdit': 'cplRulesEdit'
};

// initial state
const initialState = {
	data: null,
	showProgressBar: false,
	table: {
		columns: {
			id: {
				index: 'id',
				title: 'ID',
				direction: 'asc'
			},
			offer: {
				index: 'offer',
				title: 'Offer',
				direction: 'asc'
			},
			geo: {
				index: 'geo',
				title: 'geo',
				direction: 'asc'
			},
			affiliate: {
				index: 'affiliate',
				title: 'Affiliate',
				direction: 'asc'
			},
			created_at: {
				index: 'created_at',
				title: 'Created',
				direction: 'asc'
			},
			admin: {
				index: 'admin',
				title: 'Admin',
				direction: 'asc'
			},
			actions: {
				title: 'Actions'
			}
		},
		sortByColumn: {
			index: 'offer',
			title: 'Offer',
			direction: 'asc'
		},
		rowsNumber: 1000
	},
	form: {
		offer_id: null,
		affiliate_id: null
	},
	error: false
};

// actions
export const actions = Object.assign(
	{
		fetchData: (props) => (dispatch, getState) => {
			(http({
				url: 'v2/cpl-rules',
				method: 'get',
				actions: [
					FETCH_DATA_SUCCESS,
					FETCH_DATA_ERROR
				]
			}))(dispatch);
		},

		submitForm: (formData, ruleId = null) => (dispatch, getState) => {
			const request = http({
				url: 'v2/cpl-rules',
				method: 'post',
				actions: [
					SUBMIT_SUCCESS,
					SUBMIT_ERROR
				],
				data: {
					offer_id: get(formData, 'offer_id.0.value', null),
					geo_id: get(formData, 'geo_id.0.value', null),
					affiliate_id: get(formData, 'affiliate_id.0.value', null)
				}
			});

			return request(dispatch)
				.then(action => {
					if (action.type === SUBMIT_SUCCESS) {
						dispatch(actions.redirectToModuleIndex());
					} else if (action.payload.response.status === 400) {
						const validationsErrors = {};
						const responseErrors = action.payload.response.data;

						forEach(responseErrors, value => {
							const key = value.dataPath.slice(1);
							validationsErrors[key] = value.message;
						});

						throw new SubmissionError(validationsErrors);
					}
				});
		},

		remove: id => dispatch => {
			const request = http({
				url: `v2/cpl-rules/${id}`,
				method: 'delete',
				actions: [
					REMOVE_SUCCESS,
					REMOVE_ERROR
				]
			});

			dispatch(actions.toggleProgressBar());

			request(dispatch);
		},

		redirectToModuleIndex: () => dispatch => {
			// @todo Adhock for redux-form submit functionality.
			setTimeout(() => dispatch(replace('cpl-rules/')), 100);
		},

		sortByColumn: columnIndex => ({
			type: SORT_BY_COLUMN,
			payload: columnIndex
		}),

		showMoreRows: () => ({
			type: SHOW_MORE_ROWS
		}),

		toggleProgressBar: () => ({
			type: TOGGLE_PROGRESS_BAR
		}),

		loadOffersLookup: text => dispatch => {
			const request = http({
				url: `v2/lookups/offers?query=${text}`,
				method: 'get',
				actions: [
					FETCH_OFFERS_SUCCESS,
					FETCH_OFFERS_ERROR
				]
			});

			return request(dispatch).then(action => {
				if (action.type === FETCH_OFFERS_SUCCESS) {
					return {
						options: createSelectOptionsFromSysEnums(action.payload.data)
					};
				}
			});
		},

		loadGeosLookup: text => dispatch => {
			const request = http({
				url: `v2/lookups/locations-with-traffic?query=${text}`,
				method: 'get',
				actions: [
					FETCH_GEOS_SUCCESS,
					FETCH_GEOS_ERROR
				]
			});

			return request(dispatch).then(action => {
				if (action.type === FETCH_GEOS_SUCCESS) {
					const data = action.payload.data
						.filter(entity => !checkIsExcludedCountryCode(entity.country_code));

					return {
						options: createSelectOptionsFromSysEnums(data)
					};
				}
			});
		},

		loadAffiliatesLookup: text => dispatch => {
			const request = http({
				url: `v2/lookups/affiliates?query=${text}`,
				method: 'get',
				actions: [
					FETCH_AFFILIATES_SUCCESS,
					FETCH_AFFILIATES_ERROR
				]
			});

			return request(dispatch).then(action => {
				if (action.type === FETCH_AFFILIATES_SUCCESS) {
					return {
						options: createSelectOptionsFromSysEnums(action.payload.data)
					};
				}
			});
		}
	}
);

// reducers
const reducers = Object.assign(
	{
		[LOCATION_CHANGE]: (state, action) => {
			if (
				action.payload.pathname.indexOf('cpl-rules') !== -1 &&
				action.payload.pathname.indexOf('cpl-rules/create') === -1
			) {
				return initialState;
			}

			return state;
		},
		[FETCH_DATA_SUCCESS]: (state, action) => {
			return { ...state, data: action.payload.data, showProgressBar: false, error: false };
		},
		[FETCH_DATA_ERROR]: (state, action) => ({ ...state, error: true, showProgressBar: false }),
		[SORT_BY_COLUMN]: (state, action) => {
			const { sortByColumn, columns }  = state.table;
			const columnIndex = action.payload;
			const direction = sortByColumn['index'] == columnIndex ?
				sortByColumn.direction === 'desc' ? 'asc' : 'desc' :
				columns[columnIndex].direction;

			return {
				...state,
				table: {
					...state.table,
					sortByColumn: {
						...columns[columnIndex],
						direction
					}
				}
			};
		},
		[SHOW_MORE_ROWS]: state => ({
			...state,
			table: {
				...state.table,
				rowsNumber: state.table.rowsNumber += initialState.table.rowsNumber
			}
		}),
		[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: !state.showProgressBar }),
		[REMOVE_SUCCESS]: state => ({...state, data: null}),
		[REMOVE_ERROR]: state => ({...state, error: true}),
		[SUBMIT_ERROR]: (state, action) => {
			const { response: {status = null, data = null} } = action.payload;
			if (status === 400 && !!data) return state;
			return { ...state, error: true };
		}
	}
);
export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.cplRules.data,
	getTable: state => {
		const { table, data } = state.cplRules;
		const rows = state.cplRules.data ? sortColumns(data, table.sortByColumn) : [];

		return {
			headers: values(table.columns),
			rows: rows.slice(0, table.rowsNumber),
			sortedColumn: table.sortByColumn,
			rowsNumber: table.rowsNumber,
			totalRows: rows.length
		};
	},
	getIntl: state => intl(state),
	getProgressBar: state => state.cplRules.showProgressBar,
	getFieldsById: state => ruleId => {
		const current = find(state.cplRules.data, { id: parseInt(ruleId) });
		return current ? {
			...state.cplRules.form,
			offer_id: [{ value: current.offer_id, label: current.offer }],
			geo_id: [{ value: current.geo_id, label: current.geo }],
			affiliate_id: [{ value: current.affiliate_id, label: current.affiliate }]
		} : state.cplRules.form;
	},
	getCan: state => checkPermissions(state, allowedPermissions),
	getError: state => state.cplRules.error
};

// functions
const sortColumns = (rows, sortedColumn) => {
	if (!rows) return null;
	return orderBy(rows, row => row[sortedColumn.index], sortedColumn.direction);
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/cpl-rules/index.js